import {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import './DraggablePopup.scss';

export type DraggablePopupState = 'peek' | 'half' | 'open';

export interface DraggablePopupRef {
  setPosition: (pos: DraggablePopupState) => void;
  currentPosition: DraggablePopupState;
  onPositionChange?: (pos: DraggablePopupState) => void;
}

interface DraggablePopupProps {
  children: React.ReactNode;
  title?: string;
  customStyle?: React.CSSProperties;
  peekHeight?: number;
  halfHeightPercent?: number;
  safetyGap?: number;
  popupBottom?: number;
  initPosition?: 'open' | 'half' | 'peek';
  onPositionChange?: (pos: DraggablePopupState) => void;
}

const DraggablePopup = forwardRef<DraggablePopupRef, DraggablePopupProps>(
  (
    {
      children,
      title,
      customStyle = {},
      peekHeight = 16, // 未展開高度
      halfHeightPercent = 0.5, // 半展開百分比
      safetyGap = 16, // 上方安全間隔
      popupBottom = 0, // 底部間隔 (position: fixed 的 bottom)
      initPosition = 'peek',
      onPositionChange,
    },
    ref
  ) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const dragStartY = useRef<number | null>(null);
    const startHeight = useRef(peekHeight);
    const lastPositionState = useRef<DraggablePopupState>(initPosition);

    const [height, setHeight] = useState(0);
    const [transition, setTransition] = useState('height 0.3s ease');
    const [currentPosition, setCurrentPosition] = useState(initPosition);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const THRESHOLD = 200;

    const { fullHeight, halfHeight, snapPoints } = useMemo(() => {
      const fh = windowHeight - safetyGap - popupBottom;
      const hh = fh * halfHeightPercent;
      return {
        fullHeight: fh,
        halfHeight: hh,
        snapPoints: [peekHeight, hh, fh],
      };
    }, [windowHeight, safetyGap, popupBottom, halfHeightPercent, peekHeight]);

    /**
     * 根據高度值自動判斷對應的位置狀態
     * @param {number} h - 當前高度
     * @returns {DraggablePopupState} - 對應的位置狀態
     */
    const determinePositionByHeight = useCallback(
      (h: number): DraggablePopupState => {
        // 計算最接近哪個預設值
        const minDiff = Math.min(
          Math.abs(h - peekHeight),
          Math.abs(h - halfHeight),
          Math.abs(h - fullHeight)
        );

        if (minDiff === Math.abs(h - fullHeight)) return 'open';
        if (minDiff === Math.abs(h - halfHeight)) return 'half';
        return 'peek';
      },
      [peekHeight, halfHeight, fullHeight]
    );

    /**
     * 根據位置狀態自動判斷對應的高度值
     * @param {DraggablePopupState} pos - 當前位置狀態
     * @returns {number} - 對應的高度值
     */
    const positionToHeight = useCallback(
      (pos: DraggablePopupState): number => {
        if (pos === 'open') return fullHeight;
        if (pos === 'half') return halfHeight;
        return peekHeight;
      },
      [fullHeight, halfHeight, peekHeight]
    );

    /**
     * 設定高度
     * @param {number} target - 目標高度
     * @param {boolean} animate - 是否使用動畫
     */
    const snapTo = useCallback(
      (target: number, animate = true) => {
        // 高度無變化時不執行
        if (height === target) return;
        // 先記錄當前的位置狀態，以便在高度變化時恢復
        const currentState = determinePositionByHeight(target);
        lastPositionState.current = currentState;

        setTransition(animate ? 'height 0.3s ease' : 'none');
        setHeight(target);
      },
      [determinePositionByHeight, height]
    );
    const snapToRef = useRef(snapTo);
    useEffect(() => {
      snapToRef.current = snapTo;
    }, [snapTo]);

    // 外部可呼叫的 function
    useImperativeHandle(
      ref,
      () => ({
        setPosition: (pos: DraggablePopupState) => {
          snapToRef.current(positionToHeight(pos), true);
          setCurrentPosition(pos);
        },
        currentPosition,
        // 將父元件傳入的回調函數傳遞出去
        onPositionChange,
      }),
      [positionToHeight, currentPosition, onPositionChange]
    );

    /**
     * 根據目前高度，尋找距離最近且在閾值(THRESHOLD)內的snap point。
     * 若所有snap point距離皆超過閾值，則回傳null，即找不到最近的snap point。
     * 用於拖曳時自動吸附到預設高度(展開、半展、收起)。
     *
     * @param {number} current - 當前popup高度
     * @returns {number | null} - 返回最近的snap point高度，或無符合時回傳null
     */
    const getClosestSnapIfNear = useCallback(
      (current: number): number | null => {
        let closest: number | null = null;
        let minDiff = Infinity;
        for (const point of snapPoints) {
          const diff = Math.abs(point - current);
          if (diff < THRESHOLD && diff < minDiff) {
            closest = point;
            minDiff = diff;
          }
        }
        return closest;
      },
      [snapPoints, THRESHOLD]
    );

    const startDrag = (clientY: number) => {
      dragStartY.current = clientY;
      startHeight.current = height;
      setTransition('none');
    };

    const handleDragMove = useCallback(
      (clientY: number) => {
        if (dragStartY.current !== null) {
          const deltaY = clientY - dragStartY.current;
          const newHeight = Math.max(
            peekHeight,
            Math.min(fullHeight, startHeight.current - deltaY)
          );
          setHeight(newHeight);
        }
      },
      [peekHeight, fullHeight]
    );

    /**
     * 防抖函數 - 避免短時間內重複執行
     * @param fn 要執行的函數
     * @param delay 延遲時間 (毫秒)
     */
    const debounce = <T extends (...args: any[]) => any>(fn: T, delay: number) => {
      let timer: number | null = null;
      return function (...args: Parameters<T>) {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          fn(...args);
        }, delay);
      };
    };

    // 監聽視窗高度變化
    useEffect(() => {
      const handleResize = debounce(() => {
        setWindowHeight(window.innerHeight);
      }, 100);

      const eventTargets: Array<{ target: EventTarget; event: string }> = [
        { target: window, event: 'resize' },
      ];

      if (window.visualViewport) {
        eventTargets.push({ target: window.visualViewport, event: 'resize' });
      }

      eventTargets.forEach(({ target, event }) => {
        target.addEventListener(event, handleResize);
      });

      handleResize();

      return () => {
        eventTargets.forEach(({ target, event }) => {
          target.removeEventListener(event, handleResize);
        });
      };
    }, []);

    // 當高度變化時更新位置狀態 (對外輸出狀態)
    useEffect(() => {
      const newPosition = determinePositionByHeight(height);
      if (newPosition !== currentPosition) {
        setCurrentPosition(newPosition);
        if (onPositionChange) {
          onPositionChange(newPosition);
        }
      }
    }, [height, determinePositionByHeight, currentPosition, onPositionChange]);

    // 監聽 snapPoints 變化，根據最後位置狀態自動調整高度
    useEffect(() => {
      if (lastPositionState.current) {
        const newTargetHeight = positionToHeight(lastPositionState.current);
        snapToRef.current(newTargetHeight, true);
      }
    }, [snapPoints, positionToHeight]);

    const stopDrag = useCallback(() => {
      dragStartY.current = null;
      const target = getClosestSnapIfNear(height);
      if (target !== null) {
        snapToRef.current(target, true);
      } else {
        snapToRef.current(height, false);
      }
    }, [getClosestSnapIfNear, height, snapToRef]);

    useEffect(() => {
      const onMove = (e: any) => {
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        handleDragMove(clientY);
      };

      const onEnd = () => stopDrag();

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onEnd);
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend', onEnd);
      return () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onEnd);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onEnd);
      };
    }, [handleDragMove, stopDrag]);

    // 設定 popup 的 scrollTop > 0，避免 App 內瀏覽器誤判下滑縮小
    useEffect(() => {
      if (popupRef.current && height > 0) {
        popupRef.current.scrollTop = 1;
      }
    }, [height]);

    // 初次載入，height 會從 0 往 peekHeight 展開
    useEffect(() => {
      // 如果 peekHeight 為 0，則不展開
      if (peekHeight === 0) return;
      // 先確保 transition 為 none，height 為 0
      setTransition('none');
      setHeight(0);
      // 設定初始位置狀態
      lastPositionState.current = initPosition;
      // 下一個 event loop 再啟動動畫到 peekHeight
      const timer = setTimeout(() => {
        snapToRef.current(positionToHeight(initPosition), true);
      }, 20);
      return () => clearTimeout(timer);
    }, []);

    const handleOverlayClick = (
      e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
    ) => {
      e.stopPropagation();
      snapToRef.current(peekHeight, true);
    };
    const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => startDrag(e.clientY);
    const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => startDrag(e.touches[0].clientY);

    return (
      <>
        <div
          className="draggable-popup__overlay"
          style={{
            pointerEvents: height > peekHeight ? 'auto' : 'none',
            opacity: height > peekHeight ? 1 : 0,
            transition: 'opacity 0.3s',
          }}
          onMouseUp={(e) => handleOverlayClick(e)}
          onTouchEnd={(e) => handleOverlayClick(e)}
        />
        <div
          ref={popupRef}
          className="draggable-popup"
          style={{
            height,
            bottom: popupBottom,
            transition,
            ...customStyle,
          }}
        >
          <div
            className="draggable-popup__header"
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            style={{ cursor: 'grab' }}
          >
            <div className="draggable-popup__slider-bar" />
            {title && <h2 className="draggable-popup__header__title">{title}</h2>}
          </div>
          <div className="draggable-popup__content">{children}</div>
          <div className="draggable-popup__spacer" />
        </div>
      </>
    );
  }
);

DraggablePopup.displayName = 'DraggablePopup';
export default DraggablePopup;
